import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { UNLOCK_PAGINATION_LABELS } from "@/constants/messages";
import { UNLOCK_PER_PAGE_OPTIONS } from "@/constants/unlock-request";

interface UnlockPaginationProps {
  lastPage: number;
  rowCount: number;
  page: number;
  perPage: number;
  onPageChange: (page: number) => void;
  onPerPageChange: (perPage: number) => void;
}

export default function UnlockPagination({
  lastPage,
  rowCount,
  page,
  perPage,
  onPageChange,
  onPerPageChange,
}: UnlockPaginationProps): JSX.Element {
  const isFirst: boolean = page <= 1;
  const isLast: boolean = page >= lastPage;

  return (
    <div className="flex flex-col space-y-4 px-2 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
      <div className="flex-1 text-sm text-muted-foreground text-center sm:text-left">
        {UNLOCK_PAGINATION_LABELS.ROWS_FOUND(rowCount)}
      </div>
      <div className="flex flex-col space-y-4 sm:flex-row sm:items-center sm:space-y-0 sm:space-x-6 lg:space-x-8">
        <div className="flex items-center justify-center space-x-2 sm:justify-start">
          <p className="text-sm font-medium">
            {UNLOCK_PAGINATION_LABELS.ROWS_PER_PAGE}
          </p>
          <Select
            value={`${perPage}`}
            onValueChange={value => onPerPageChange(Number(value))}
          >
            <SelectTrigger className="h-8 w-[70px]">
              <SelectValue placeholder={`${perPage}`} />
            </SelectTrigger>
            <SelectContent side="top">
              {UNLOCK_PER_PAGE_OPTIONS.map(pageSize => (
                <SelectItem key={pageSize} value={`${pageSize}`}>
                  {pageSize}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center justify-center text-sm font-medium sm:w-[100px]">
          {UNLOCK_PAGINATION_LABELS.PAGE_OF(page, lastPage)}
        </div>
        <div className="flex items-center justify-center space-x-2 sm:justify-start">
          <Button
            variant="outline"
            className="h-8 w-8 p-0 sm:flex lg:flex"
            onClick={() => onPageChange(1)}
            disabled={isFirst}
          >
            <span className="sr-only">{UNLOCK_PAGINATION_LABELS.FIRST}</span>
            <ChevronsLeft />
          </Button>
          <Button
            variant="outline"
            className="h-8 w-8 p-0"
            onClick={() => onPageChange(Math.max(1, page - 1))}
            disabled={isFirst}
          >
            <span className="sr-only">{UNLOCK_PAGINATION_LABELS.PREVIOUS}</span>
            <ChevronLeft />
          </Button>
          <Button
            variant="outline"
            className="h-8 w-8 p-0"
            onClick={() => onPageChange(page + 1)}
            disabled={isLast}
          >
            <span className="sr-only">{UNLOCK_PAGINATION_LABELS.NEXT}</span>
            <ChevronRight />
          </Button>
          <Button
            variant="outline"
            className="h-8 w-8 p-0 sm:flex lg:flex"
            onClick={() => onPageChange(lastPage)}
            disabled={isLast}
          >
            <span className="sr-only">{UNLOCK_PAGINATION_LABELS.LAST}</span>
            <ChevronsRight />
          </Button>
        </div>
      </div>
    </div>
  );
}
